import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import type { LookupCategory } from '@/features/settings/hooks/useSystemLookups';

// ─── Configuration ──────────────────────────────────────────────────────────────

/**
 * Special lookup categories that resolve from dedicated API endpoints
 * instead of the system_lookups table.
 */
export const SPECIAL_LOOKUP_ENDPOINTS: Record<string, string> = {
  contacts: '/contacts?pageSize=100&contact_type_nature=client',
  users_partner: '/users?pageSize=200&status=active&job_role=partner',
  users_manager: '/users?pageSize=200&status=active&job_role=manager',
  users_accountant: '/users?pageSize=200&status=active&job_role=accountant',
  workflow_templates: '/settings/workflow-templates',
  tax_rates: '/settings/tax-rates',
  entity_types: '/entity-types',
  tags: '/tags',
  teams: '/teams',
};

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface LookupOption {
  value: string;
  label: string;
}

export interface UseLookupValuesResult {
  options: LookupOption[];
  isLoading: boolean;
}

// ─── Mapper Functions ───────────────────────────────────────────────────────────

/**
 * Maps raw API data for a special lookup category into standardized options.
 */
function mapSpecialData(category: string, data: Record<string, unknown>[]): LookupOption[] {
  if (category === 'contacts') {
    return data.map((item) => {
      const name =
        `${item.first_name || ''} ${item.last_name || ''}`.trim() ||
        (item.email as string) ||
        (item.id as string);
      const code = (item.client_code || item.clientCode || '') as string;
      return {
        value: item.id as string,
        label: code ? `${name} (${code})` : name,
      };
    });
  }

  if (category === 'workflow_templates') {
    return data
      .filter((item) => item.is_active !== false)
      .map((item) => ({
        value: item.id as string,
        label: (item.name || item.id) as string,
      }));
  }

  if (category === 'tax_rates') {
    return data
      .filter((item) => item.is_active !== false)
      .map((item) => {
        const rate = typeof item.rate === 'string' ? parseFloat(item.rate) : (item.rate as number);
        const ratePercent = rate < 1 ? (rate * 100).toFixed(2) : rate.toFixed(2);
        const province = item.province ? ` (${item.province})` : '';
        return {
          value: item.id as string,
          label: `${item.name || 'Tax'} ${ratePercent}%${province}`,
        };
      });
  }

  if (category === 'entity_types') {
    return data.map((item) => ({
      value: item.id as string,
      label: (item.name || item.id) as string,
    }));
  }

  if (category === 'tags') {
    return data.map((item) => ({
      value: item.id as string,
      label: (item.name || item.id) as string,
    }));
  }

  if (category === 'teams') {
    return data
      .filter((item) => item.is_active !== false)
      .map((item) => ({
        value: item.id as string,
        label: (item.name || item.id) as string,
      }));
  }

  // users_partner, users_manager, users_accountant — all resolve to users
  return data.map((item) => ({
    value: item.id as string,
    label: (item.name || item.email || item.id) as string,
  }));
}

// ─── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Resolves lookup values for a given lookup category.
 *
 * This is the single source of truth for resolving field values across the app.
 * It mirrors the DynamicFormRenderer's LookupSelect logic:
 * - Special categories (contacts, users_*, workflow_templates, tax_rates, entity_types, tags)
 *   are fetched from their dedicated API endpoints.
 * - Standard categories are fetched from the system-lookups API.
 * - Fields with static options use those directly.
 * - Fields without a lookup_category (text fields) use a backend fallback to fetch
 *   distinct values from the database (when fieldKey is provided).
 *
 * @param lookupCategory - The lookup_category from the field library (e.g., 'contacts', 'job_types')
 * @param options.enabled - Whether the query should execute (default: true when category is provided)
 * @param options.staticOptions - Static options from field_library (used when no lookup_category)
 * @param options.fieldKey - The field_key for backend fallback (used when no lookup_category and no static options)
 */
export function useLookupValues(
  lookupCategory: string | null | undefined,
  {
    enabled = true,
    staticOptions,
    fieldKey,
  }: {
    enabled?: boolean;
    staticOptions?: LookupOption[];
    fieldKey?: string;
  } = {},
): UseLookupValuesResult {
  const category = lookupCategory ?? '';
  const isSpecial = category in SPECIAL_LOOKUP_ENDPOINTS;
  const isEnabled = enabled && !!category;
  const hasStaticOptions = staticOptions && staticOptions.length > 0;
  // Categories that don't exist in system_lookups and aren't in the special
  // endpoint map are resolved via the data-rules field-values endpoint (keyed
  // by field_key), which knows how to resolve them (e.g. 'saved_reports').
  const RESOLVER_ONLY_CATEGORIES = ['saved_reports'];
  const usesFieldValuesResolver =
    isEnabled && !isSpecial && RESOLVER_ONLY_CATEGORIES.includes(category) && !!fieldKey;
  const needsFallback =
    (enabled && !category && !hasStaticOptions && !!fieldKey) || usesFieldValuesResolver;

  // Standard system lookups — excluded for special endpoints and resolver-only
  // categories (which resolve via the data-rules field-values endpoint below).
  const { data: lookups, isLoading: lookupsLoading } = useSystemLookups(
    category as LookupCategory,
    isEnabled && !isSpecial && !usesFieldValuesResolver,
  );

  // Special lookups (users, contacts, workflow templates, tax rates, entity types, tags)
  const { data: specialData, isLoading: specialLoading } = useQuery({
    queryKey: ['lookup-special', category],
    queryFn: async () => {
      const endpoint = SPECIAL_LOOKUP_ENDPOINTS[category];
      const response = await apiClient.get<ApiResponse<unknown>>(endpoint);
      const payload = response.data.data;
      // Most special endpoints return a plain array; a few wrap it (e.g. /teams
      // returns { teams: [...] }). Normalise to an array of records.
      if (Array.isArray(payload)) return payload as Record<string, unknown>[];
      if (payload && typeof payload === 'object') {
        const wrapped = (payload as Record<string, unknown>)[category];
        if (Array.isArray(wrapped)) return wrapped as Record<string, unknown>[];
        // Fall back to the first array-valued property.
        const firstArray = Object.values(payload as Record<string, unknown>).find((v) =>
          Array.isArray(v),
        );
        if (Array.isArray(firstArray)) return firstArray as Record<string, unknown>[];
      }
      return [] as Record<string, unknown>[];
    },
    enabled: isEnabled && isSpecial,
  });

  // Fallback: fetch distinct values from the backend for text fields without lookup_category
  const { data: fallbackData, isLoading: fallbackLoading } = useQuery({
    queryKey: ['data-rules', 'field-values', fieldKey],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<LookupOption[]>>(
        `/data-rules/field-values?field=${encodeURIComponent(fieldKey!)}`,
      );
      return response.data.data || [];
    },
    enabled: needsFallback,
  });

  const isLoading = needsFallback ? fallbackLoading : isSpecial ? specialLoading : lookupsLoading;

  const options = useMemo((): LookupOption[] => {
    // Static options take priority
    if (hasStaticOptions) return staticOptions!;

    // Fallback for text fields
    if (needsFallback) return fallbackData || [];

    if (!isEnabled) return [];

    if (isSpecial) {
      if (!specialData) return [];
      return mapSpecialData(category, specialData);
    }

    // Standard system lookups
    if (!lookups) return [];
    return lookups
      .filter((l) => l.isActive)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((l) => ({ value: l.code, label: l.label }));
  }, [
    isEnabled,
    isSpecial,
    specialData,
    lookups,
    category,
    hasStaticOptions,
    staticOptions,
    needsFallback,
    fallbackData,
  ]);

  return { options, isLoading };
}
